/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import i18n from "../../i18";
import dayjs from "dayjs";
import Nursery from "../Images/Nursery.png";
import PrimaryShool from "../Images/PrimaryShool.png";
import SecondarySchool from "../Images/SecondarySchool.png";

const GetReportsTuitionPayments = () => {
  const { t } = useTranslation();
  const [dataSource, setDataSource] = React.useState([]);
  const [classFilter, setClassFilter] = React.useState("all");
  const [sectionFilter, setSectionFilter] = React.useState("all");
  const [selectedMonth, setSelectedMonth] = React.useState(dayjs());
  const [selectedDate, setSelectedDate] = React.useState(dayjs());
  const [showCustomDate, setShowCustomDate] = React.useState(false);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = React.useState(true);
  const [selectedImage, setSelectedImage] = React.useState(Nursery);
  const user = JSON.parse(localStorage.getItem("user"));
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
  React.useEffect(() => {
    getApiProducts();
  }, [token, selectedMonth]);

  React.useEffect(() => {
    const loadUserData = () => {
      const branchId = user?.branch_id || 2;
      const branchImages = {
        1: Nursery,
        2: PrimaryShool,
        3: SecondarySchool,
      };
      setSelectedImage(branchImages[branchId] || Nursery);
    };

    loadUserData();
  }, [user]);

  const getApiProducts = async () => {
    try {
      const formattedMonth = selectedMonth.format("MM-YYYY");
      const res = await fetch(
        `https://api.clever-school.com/api/reports/tuition-payments-report?month=${formattedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const responseData = await res.json();
      if (Array.isArray(responseData)) {
        setDataSource(responseData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = dataSource.filter((payment) => {
    const paymentDate = dayjs(payment.payment_date);
    const classMatch =
      classFilter === "all" || JSON.parse(payment.class).en === classFilter;
    const sectionMatch =
      sectionFilter === "all" ||
      JSON.parse(payment.section).en === sectionFilter;
    const dateMatch =
      !showCustomDate ||
      (paymentDate.isSame(selectedDate, "day") &&
        paymentDate.isSame(selectedMonth, "month"));

    return classMatch && sectionMatch && dateMatch;
  });

  const totalAmountPaid = filteredData.reduce(
    (sum, payment) => sum + parseFloat(payment.amount_paid || 0),
    0
  );

  // Table columns configuration
  const columns = [
    { title: "#", render: (_, __, index) => index + 1 },
    { title: t("studentId"), dataIndex: "student_id" },
    { title: t("name"), dataIndex: "student_name" },
    {
      title: t("class"),
      dataIndex: "class",
      render: (text) => JSON.parse(text)?.en || text,
    },
    {
      title: t("section"),
      dataIndex: "section",
      render: (text) => JSON.parse(text)?.en || text,
    },
    {
      title: t("amountPaid"),
      dataIndex: "amount_paid",
      render: (text) => parseFloat(text).toLocaleString("en-US"),
    },
    { title: t("paymentDate"), dataIndex: "payment_date" },
  ];

  // Print styles with large font sizes (same as GetStudentReport)
  const printStyles = `
@media print {
     * {
        font-size: 24px !important;
      }
      
     html, body, div, span, table, tr, td, th, p, h1, h2, h3, h4, h5, h6 {
        font-size: 24px !important;
      }
      
     body, html {
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        font-size: 24px !important;
        line-height: 1.5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        background-color: white !important;
        direction: ${isRTL ? 'rtl' : 'ltr'} !important;
        text-align: ${isRTL ? 'right' : 'left'} !important;
      }

    body * {
      visibility: hidden;
      font-size: 24px !important;
    }

    .print-container, .print-container * {
      visibility: visible;
      background-color: white !important;
      font-size: 24px !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
      text-align: ${isRTL ? 'right' : 'left'} !important;
    }

    .print-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: auto;
      padding: 0;
      margin: 0;
    }

    .print-header {
      display: block !important;
      text-align: ${isRTL ? 'right' : 'center'};
      margin-bottom: 20px;
      page-break-after: avoid;
      font-size: 24px !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }

    .print-title {
      font-size: 32px !important;
      font-weight: bold !important;
      color: #333 !important;
      margin: 20px 0 !important;
    }

    .no-print {
      display: none !important;
    }

    .table-container {
      width: 100% !important;
      margin: 0 !important;
      overflow: visible !important;
      page-break-inside: auto;
    }

    table {
      width: 100% !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
      font-size: 24px !important;
      border: none !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
      border-radius: 8px !important;
      overflow: hidden !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }

    th {
      background: #000080 !important;
      color: #ffffff !important;
      font-weight: 700 !important;
      font-size: 20px !important;
      text-align: ${isRTL ? 'right' : 'center'} !important;
      text-transform: uppercase !important;
      letter-spacing: 0.8px !important;
      border-bottom: 2px solid #dee2e6 !important;
      position: relative !important;
    }
    th, th * {
      background: #000080 !important;
      color: #ffffff !important;
      font-size: 20px !important;
    }
    th:first-child {
      border-top-left-radius: 8px !important;
    }
    th:last-child {
      border-top-right-radius: 8px !important;
    }
    thead {
      display: table-header-group !important;
    }
    tbody tr:nth-child(even) {
      background-color: #fafbfc !important;
    }
    tbody tr:nth-child(odd) {
      background-color: white !important;
    }
    tbody tr:hover {
      background-color: #f8f9fa !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
    }
    .total-row {
      background: #ffffff !important;
      color: #2c3e50 !important;
      font-weight: 700 !important;
      border-bottom: none !important;
      position: relative !important;
    }
    .total-row::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      height: 3px !important;
    }
    .total-row td {
      background: transparent !important;
      color: #2c3e50 !important;
      font-weight: 700 !important;
      font-size: 24px !important;
      border: none !important;
      padding: 20px !important;
      text-align: ${isRTL ? 'right' : 'left'} !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }
    .total-row td:first-child {
      border-bottom-left-radius: 8px !important;
    }
    .total-row td:last-child {
      border-bottom-right-radius: 8px !important;
    }
    @page {
      size: A4;
      margin: 8mm;
    }
    .print-container th,
    .print-container th * {
      font-size: 20px !important;
      background: #000080 !important;
      color: #ffffff !important;
    }
    .print-container td,
    .print-container td * {
      font-size: 24px !important;
    }
    .print-container table {
      font-size: 24px !important;
    }
    .print-container table td {
      font-size: 24px !important;
    }
    .print-container table th {
      font-size: 20px !important;
    }

    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }

    td, th {
      padding: 16px 20px !important;
      border: none !important;
      font-size: 24px !important;
      text-align: ${isRTL ? 'right' : 'left'} !important;
      vertical-align: middle !important;
      border-bottom: 1px solid #f0f0f0 !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }
    td {
      font-size: 24px !important;
    }
    .print-container td, .print-container th {
      font-size: 24px !important;
    }
    .print-container th {
      font-size: 20px !important;
    }
  }

  .print-header {
    display: none;
  }
`;

  const handlePrint = () => {
    window.print();
  };

  const getCurrentAcademicYear = () => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${currentYear + 1}`;
  };

  const branchTitleMap = {
    1: "باخچەی منداڵانی کلیڤەری ناحکومی",
    2: "ناوەندیی کلیڤەری ناحکومی",
    3: "ئامادەی کلیڤەری ناحکومی",
  };
  const branchTitle = branchTitleMap[user?.branch_id] || "قوتابخانەی کلیڤەری بنەڕەتی ناحکومی";
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <style>{printStyles}</style>
      <Box 
        className="print-container"
        sx={{ 
          direction: isRTL ? "rtl" : "ltr", 
          textAlign: isRTL ? "right" : "left",
          padding: 2 
        }}
      >
        {/* Print Header */}
        <Box className="print-header">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
            <img 
              src={selectedImage} 
              alt="School Logo" 
              style={{ 
                width: "80px", 
                height: "80px", 
                marginRight: isRTL ? "0" : "20px",
                marginLeft: isRTL ? "20px" : "0"
              }} 
            />
            <Box>
              <Typography variant="h4" className="print-title" sx={{ fontSize: "32px", fontWeight: "bold", color: "#333" }}>
                {branchTitle}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: "20px", color: "#666", mt: 1 }}>
                {t("tuitionPaymentsReport")} - {getCurrentAcademicYear()}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Grid container spacing={2} mb={2} className="no-print">
          {/* Class Filter */}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label={t("class")}
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="all">{t("allClasses")}</option>
              {[...Array(12).keys()].map((n) => (
                <option key={n + 1} value={n + 1}>
                  {t("class")} {n + 1}
                </option>
              ))}
            </TextField>
          </Grid>

          {/* Section Filter */}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label={t("section")}
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="all">{t("allSections")}</option>
              {["A", "B", "C", "D", "E", "F", "G", "H", "I"].map((sec) => (
                <option key={sec} value={sec}>
                  {t("section")} {sec}
                </option>
              ))}
            </TextField>
          </Grid>

          {/* Date Selection Controls */}
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant={showCustomDate ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                const newDate = selectedMonth.isSame(dayjs(), "month")
                  ? dayjs()
                  : selectedMonth.startOf("month");
                setSelectedDate(newDate);
                setShowCustomDate(true);
              }}
              sx={{ height: "56px" }}
            >
              {t("Today")}
            </Button>
          </Grid>

          <Grid item xs={12} sm={3}>
            <DatePicker
              views={["year", "month"]}
              label={t("selectMonth")}
              value={selectedMonth}
              onChange={(newValue) => {
                setSelectedMonth(newValue);
                setShowCustomDate(false);
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>

          {/* Day Picker - any day selectable; changing day to another month updates selected month */}
          {showCustomDate && (
            <Grid item xs={12} sm={3}>
              <DatePicker
                label={t("selectDay")}
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue);
                    if (!newValue.isSame(selectedMonth, "month")) {
                      setSelectedMonth(newValue.startOf("month"));
                    }
                  }
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          )}
        </Grid>

        {/* Print Button */}
        <Box sx={{ mb: 2, textAlign: "center" }} className="no-print">
          <Button
            variant="contained"
            onClick={handlePrint}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              px: 4,
              py: 1.5,
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {t("print")}
          </Button>
        </Box>

        {/* Data Table */}
        <TableContainer
          component={Paper}
          className="table-container"
          sx={{ 
            borderRadius: 2, 
            boxShadow: 3,
            overflow: "auto",
            maxHeight: "70vh"
          }}
        >
          <Table 
            sx={{ 
              direction: isRTL ? "rtl" : "ltr",
              "& .MuiTableCell-root": {
                border: "none",
                fontSize: "16px",
                padding: "12px 16px",
              }
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.dataIndex || column.title}
                    sx={{ 
                      backgroundColor: "#000080",
                      color: "#ffffff",
                      textAlign: isRTL ? "right" : "center",
                      fontSize: "18px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px"
                    }}
                  >
                    {column.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, index) => (
                <TableRow
                  key={`${row.student_id}-${index}`}
                  hover
                  sx={{ 
                    "&:last-child td": { borderBottom: 0 },
                    backgroundColor: index % 2 === 0 ? "#fafbfc" : "#ffffff",
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                    },
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  {columns.map((column) => (
                    <TableCell 
                      key={column.dataIndex || column.title}
                      sx={{
                        textAlign: isRTL ? "right" : "left",
                        fontSize: "16px",
                        padding: "12px 16px",
                        borderBottom: "1px solid #f0f0f0"
                      }}
                    >
                      {column.render
                        ? column.render(row[column.dataIndex], row, index)
                        : row[column.dataIndex]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Total Row */}
              <TableRow 
                className="total-row"
                sx={{
                  backgroundColor: "#ffffff",
                  "@media print": {
                    backgroundColor: "#ffffff !important",
                  },
                }}
              >
                <TableCell
                  colSpan={5}
                  align="right"
                  sx={{
                    background: "#ffffff",
                    color: "#2c3e50",
                    fontWeight: 700,
                    fontSize: "20px",
                    padding: "20px",
                    border: "none",
                    borderTop: "3px solid #2196f3",
                    position: "relative",
                    textAlign: isRTL ? "right" : "left",
                    "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                      border: "none !important",
                      borderTop: "3px solid #2196f3 !important",
                      fontSize: "24px !important",
                    },
                  }}
                >
                  {t("totalAmountPaid")}
                </TableCell>
                <TableCell 
                  sx={{
                    background: "#ffffff",
                    color: "#2c3e50",
                    fontWeight: 700,
                    fontSize: "20px",
                    padding: "20px",
                    border: "none",
                    borderTop: "3px solid #2196f3",
                    position: "relative",
                    textAlign: isRTL ? "right" : "left",
                    "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                      border: "none !important",
                      borderTop: "3px solid #2196f3 !important",
                      fontSize: "24px !important",
                    },
                  }}
                >
                  {totalAmountPaid.toLocaleString("en-US")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default GetReportsTuitionPayments;
